// @flow
import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { log } from '../../utils/jsUtils';
import TagReadonly from './TagReadonly';
import TagEditable from './TagEditable';
import tagRendererStyles from './Styles/TagRenderer.Style';
import { selectAllTags, selectFeedRequests } from '../../reduxStore/modules/tags/selectors';
import { useI18n } from '../../contexts/I18nContext';
import { selectRequests } from '../../reduxStore/modules/feed';

// Enable logging for component in development only
const DEBUG_ENABLED = process.env.NODE_ENV !== 'production';

type Props = {
  customerId?: string,
  tagsIds?: Array<string>,
  readonly?: boolean,
  debug?: boolean,
  containerStyle?: Object,
  tagUpdateCounter?: number,
  onTagRemoved?: Function,
  onTagAdded?: Function,
  size?: 'small' | 'medium',
  // Legacy props for backwards compatibility
  callbackOnTagPicked?: Function,
  callbackOnTagRemoved?: Function
};

// Use shallowEqual for array comparison
const tagsEqual = (arr1, arr2) => {
  if (!arr1 || !arr2) return false;
  if (arr1.length !== arr2.length) return false;
  
  // Convert both arrays to sorted string arrays for comparison
  // Make sure to convert each element to string for consistent comparison
  const normalizedArr1 = [...arr1].map(id => String(id)).sort();
  const normalizedArr2 = [...arr2].map(id => String(id)).sort();
  
  // Compare each element
  for (let i = 0; i < normalizedArr1.length; i++) {
    if (normalizedArr1[i] !== normalizedArr2[i]) {
      return false;
    }
  }
  
  return true;
};

// Create a custom comparison function for React.memo
const arePropsEqual = (prevProps, nextProps) => {
  // Check for primitive props changes
  if (
    prevProps.customerId !== nextProps.customerId ||
    prevProps.readonly !== nextProps.readonly ||
    prevProps.debug !== nextProps.debug ||
    prevProps.tagUpdateCounter !== nextProps.tagUpdateCounter ||
    prevProps.size !== nextProps.size
  ) {
    return false;
  }
  
  // Compare tagsIds arrays
  if (!tagsEqual(prevProps.tagsIds, nextProps.tagsIds)) {
    return false;
  }
  
  // Compare containerStyle objects if they exist
  if (
    (prevProps.containerStyle && nextProps.containerStyle) &&
    JSON.stringify(prevProps.containerStyle) !== JSON.stringify(nextProps.containerStyle)
  ) {
    return false;
  }
  
  // Compare callback function references
  if ((prevProps.onTagRemoved && !nextProps.onTagRemoved) || 
      (!prevProps.onTagRemoved && nextProps.onTagRemoved)) {
    return false;
  }
  
  if ((prevProps.onTagAdded && !nextProps.onTagAdded) || 
      (!prevProps.onTagAdded && nextProps.onTagAdded)) {
    return false;
  }

  if ((prevProps.callbackOnTagPicked && !nextProps.callbackOnTagPicked) || 
      (!prevProps.callbackOnTagPicked && nextProps.callbackOnTagPicked)) {
    return false;
  }

  if ((prevProps.callbackOnTagRemoved && !nextProps.callbackOnTagRemoved) || 
      (!prevProps.callbackOnTagRemoved && nextProps.callbackOnTagRemoved)) {
    return false;
  }
  
  return true;
};

const TagRenderer = ({
  customerId,
  tagsIds,
  readonly = false,
  debug = false,
  containerStyle = {},
  tagUpdateCounter = 0,
  onTagRemoved,
  onTagAdded,
  size = 'medium',
  // Legacy props for backwards compatibility
  callbackOnTagPicked,
  callbackOnTagRemoved
}: Props) => {
  const i18n = useI18n();
  const allTags = useSelector(selectAllTags);
  const feedRequests = useSelector(selectRequests);
  const [renderedTagIds, setRenderedTagIds] = useState([]);
  
  // Find the customer in the feed data (for customerId mode)
  const customer = useMemo(() => {
    if (!feedRequests?.length || !customerId) return null;
    
    return feedRequests.find(req => 
      req.lookupTenant && req.lookupTenant._id === customerId
    )?.lookupTenant || null;
  }, [feedRequests, customerId]);
  
  // Extract tag IDs from either tagsIds prop or customer data
  const tagIds = useMemo(() => {
    if (tagsIds && Array.isArray(tagsIds)) {
      // Direct tagsIds mode (for TagsContent usage)
      return tagsIds;
    }
    // Customer-based mode (for feed/tenant usage)
    return customer?.Tags || [];
  }, [tagsIds, customer]);
  
  // Update renderedTagIds only when tagIds actually change
  useEffect(() => {
    if (!tagsEqual(renderedTagIds, tagIds)) {
      if (debug) {
        log('[TagRenderer] Tags changed, updating rendered tags', {
          mode: tagsIds ? 'direct' : 'customer',
          prevIds: renderedTagIds,
          newIds: tagIds,
          added: tagIds.length > renderedTagIds.length,
          removed: tagIds.length < renderedTagIds.length
        });
      }
      setRenderedTagIds(tagIds);
    }
  }, [tagIds, tagUpdateCounter, renderedTagIds, debug, tagsIds]); // Include tagsIds to force updates when needed
  
  // Match tag IDs with their full tag objects
  const matchedTags = useMemo(() => {
    return renderedTagIds
      .map(tagId => allTags.find(t => String(t._id) === String(tagId)))
      .filter(Boolean);
  }, [renderedTagIds, allTags]);

  // Handle tag removal - support both new and legacy callbacks
  const handleTagRemoved = useCallback((tag) => {
    if (!tag || !tag._id) return;
    
    // Update local state immediately (visual feedback)
    setRenderedTagIds(prevIds => 
      prevIds.filter(id => String(id) !== String(tag._id))
    );
    
    // Call parent callback if provided (new pattern)
    if (onTagRemoved) {
      onTagRemoved(tag);
    }
    
    // Call legacy callback if provided (backwards compatibility)
    if (callbackOnTagRemoved) {
      callbackOnTagRemoved(tag);
    }
  }, [onTagRemoved, callbackOnTagRemoved]);
  
  // Handle tag addition - optimistically update UI and support both new and legacy callbacks
  const handleTagAdded = useCallback((tag) => {
    if (!tag || !tag._id) return;
    
    // Check if tag is already in the list to prevent duplicates
    const tagIdStr = String(tag._id);
    const normalizedExistingIds = renderedTagIds.map(id => String(id));
    
    if (!normalizedExistingIds.includes(tagIdStr)) {
      if (debug) {
        log('[TagRenderer] Optimistically adding tag:', {
          tagId: tag._id,
          tagName: tag.Name || tag.name,
          existingTagsCount: renderedTagIds.length
        });
      }
      
      // Update local state immediately for optimistic UI update
      setRenderedTagIds(prevIds => [...prevIds, tag._id]);
      
      // Call parent callback if provided (new pattern)
      if (onTagAdded) {
        onTagAdded(tag);
      }
      
      // Call legacy callback if provided (backwards compatibility)
      if (callbackOnTagPicked) {
        callbackOnTagPicked(tag);
      }
    } else if (debug) {
      log('[TagRenderer] Tag already exists, not adding:', {
        tagId: tag._id,
        tagName: tag.Name || tag.name
      });
    }
  }, [renderedTagIds, debug, onTagAdded, callbackOnTagPicked]);
  
  // No customer ID and no direct tagsIds, nothing to render
  if (!customerId && !tagsIds) return null;
  
  // For readonly mode
  if (readonly) {
    // No tags to show
    if (matchedTags.length === 0) {
      return null;
    }
    
    return (
      <Box
        sx={tagRendererStyles.tagsContainer}
        style={containerStyle}
      >
        {matchedTags.map(tag => (
          <TagReadonly
            key={`tag-${tag._id}`}
            tag={tag}
            customerId={customerId}
            readonly={true}
            debug={debug}
            size={size}
            onTagRemoved={handleTagRemoved}
          />
        ))}
      </Box>
    );
  }

  // Standalone editable mode: tagsIds + onTagRemoved without customerId
  // Used for local state management (e.g., broadcast dialog tag selection)
  if (!customerId && tagsIds && onTagRemoved) {
    if (matchedTags.length === 0) {
      return null;
    }

    return (
      <Box
        sx={tagRendererStyles.tagsContainer}
        style={containerStyle}
      >
        {matchedTags.map(tag => (
          <TagReadonly
            key={`tag-${tag._id}`}
            tag={tag}
            readonly={false}
            debug={debug}
            size={size}
            onTagRemoved={handleTagRemoved}
          />
        ))}
      </Box>
    );
  }

  // For editable mode with customerId (requires customer context for backend operations)
  if (!customerId) {
    return null;
  }

  // No tags to render - show only add button
  if (matchedTags.length === 0) {
    return (
      <Box
        sx={tagRendererStyles.tagsContainer}
        style={containerStyle}
      >
        <TagEditable
          customerId={customerId}
          showOnlyAddButton={true}
          debug={debug}
          onTagAdded={handleTagAdded}
        />
      </Box>
    );
  }

  // For editable mode, show tags with remove buttons and Add Tag button
  return (
    <Box
      sx={tagRendererStyles.tagsContainer}
      style={containerStyle}
    >
      {matchedTags.map(tag => (
        <TagReadonly
          key={`tag-${tag._id}`}
          tag={tag}
          customerId={customerId}
          readonly={false}
          debug={debug}
          size={size}
          onTagRemoved={handleTagRemoved}
        />
      ))}

      <TagEditable
        customerId={customerId}
        showOnlyAddButton={true}
        debug={debug}
        onTagAdded={handleTagAdded}
      />
    </Box>
  );
};

// Wrap in memo with custom comparison function to prevent unnecessary re-renders
export default React.memo(TagRenderer, arePropsEqual);